import React from 'react';
import PropTypes from 'prop-types';
import { isString } from 'lodash';
import { Stack, Typography, Alert } from '@metarouter-private/mr-mui';
import { AceCodeEditor } from '@ion/components';

const CIRCUIT_BREAKER_ERROR = 'circuit breaker open';

const CIRCUIT_BREAKER_MESSAGE =
  'Delivery was skipped because the circuit breaker is open. After 5 consecutive failures, ' +
  'deliveries are paused for 5 minutes to protect the endpoint. A probe request will be ' +
  'attempted automatically after the cooldown period. If it succeeds, normal delivery resumes.';

export default function DeliveryLogsExpandedRow({ data }) {
  const { errorMessage, responseStatus, resolvedIp, requestPayload, responseBody } = data;
  const displayMessage = getErrorDisplayMessage({ errorMessage, responseStatus });

  return (
    <Stack spacing={2} padding={2}>
      {displayMessage && <Alert severity="warning">{displayMessage}</Alert>}
      <Stack spacing={0.5}>
        <Typography variant="subtitle2">Resolved IP</Typography>
        <Typography variant="body2">{resolvedIp ?? '—'}</Typography>
      </Stack>
      <JsonBlock label="Request Payload" value={requestPayload} />
      <JsonBlock label="Response Body" value={responseBody} />
    </Stack>
  );
}

function JsonBlock({ label, value }) {
  const formatted = formatJson(value);

  return (
    <Stack spacing={0.5}>
      <Typography variant="subtitle2">{label}</Typography>
      {formatted ? (
        <AceCodeEditor name={label} lang="json" value={formatted} readOnly rows={20} />
      ) : (
        <Typography variant="body2">—</Typography>
      )}
    </Stack>
  );
}

DeliveryLogsExpandedRow.propTypes = {
  data: PropTypes.shape({
    errorMessage: PropTypes.string,
    responseStatus: PropTypes.number,
    resolvedIp: PropTypes.string,
    requestPayload: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    responseBody: PropTypes.string,
  }).isRequired,
};

function getErrorDisplayMessage({ errorMessage, responseStatus }) {
  if (!errorMessage) {
    return null;
  }

  if (errorMessage === CIRCUIT_BREAKER_ERROR) {
    return CIRCUIT_BREAKER_MESSAGE;
  }

  const statusMessage = getStatusMessage(responseStatus);
  if (statusMessage) {
    return statusMessage;
  }

  return errorMessage;
}

const STATUS_MESSAGES = {
  400: 'Request was malformed. Check your webhook configuration.',
  401: 'Authentication required. Check your endpoint credentials.',
  403: 'Access denied. Check your endpoint permissions.',
  404: 'Endpoint not found. Verify the URL is correct.',
  405: 'Endpoint does not accept POST requests.',
  408: 'Request timed out.',
  429: 'Rate limited. Deliveries will retry automatically.',
  500: 'Internal server error on the endpoint.',
  502: 'Endpoint temporarily unavailable (Bad Gateway).',
  503: 'Endpoint temporarily unavailable (Service Unavailable).',
  504: 'Endpoint timed out (Gateway Timeout).',
};

function getStatusMessage(status) {
  if (status === null) {
    return null;
  }

  if (STATUS_MESSAGES[status]) {
    return STATUS_MESSAGES[status];
  }

  if (status >= 400 && status < 500) {
    return `Client error (${status}).`;
  }
  if (status >= 500) {
    return `Server error (${status}).`;
  }

  return null;
}

JsonBlock.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

function formatJson(value) {
  if (value === null) {
    return null;
  }

  try {
    const parsed = isString(value) ? JSON.parse(value) : value;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return String(value);
  }
}
